// Merchant · Storefront Setup Guide (MERCHANT-ONBOARDING-REVIEW-1 Phase 2)
// Guided onboarding for merchants with no Storefront yet — 6-step stepper
// backed by existing endpoints:
//   POST /api/v1/merchant/storefronts            (create — step 1-3 payload)
//   GET  /api/v1/merchant/storefronts            (list — has-one check)
//   GET  /api/v1/merchant/storefront/readiness   (authoritative readiness)
//   GET  /api/v1/merchant/storefronts/{sid}/checkout-verification
// No new backend, no duplicate readiness engine.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Store, Rocket, ArrowRight, ArrowLeft, MapPin, Layers, Globe,
  ShieldCheck, ShieldAlert, CheckCircle2, AlertCircle, Circle, Info,
  Sparkles,
} from "lucide-react";

const ALL_PROV = ["ON","QC","BC","AB","MB","SK","NS","NB","NL","PE","YT","NT","NU"];
const PRIMARY_CATEGORIES = [
  { v: "retail", l: "Retail" },
  { v: "food", l: "Food & drink" },
  { v: "beauty", l: "Beauty & personal care" },
  { v: "services", l: "Services" },
  { v: "digital", l: "Digital / SaaS" },
  { v: "travel", l: "Travel" },
  { v: "other", l: "Other" },
];

// One-click starter templates — merchants tune them in the same step,
// nothing is auto-published. `sample_offer` is written to localStorage
// so the Manual Campaign creator can prefill it on first open.
const TEMPLATES = {
  retail: {
    label: "Retail example",
    icon: Store,
    identity: {
      name: "ABC Fashion",
      primary_category: "retail",
      description: "Independent apparel and footwear brand serving trend-forward shoppers.",
    },
    categories: ["Men", "Women", "Shoes", "Accessories"],
    sample_offer: {
      name: "20% off Running Shoes",
      description: "Save 20% on all running shoes — a great intro offer for new Cyannect shoppers.",
      redemption_method: "COUPON_CODE",
      discount_type: "percent",
      discount_value: 20,
    },
  },
  saas: {
    label: "SaaS example",
    icon: Sparkles,
    identity: {
      name: "Example AI",
      primary_category: "digital",
      description: "AI productivity platform sold as subscriptions and credit packs.",
    },
    categories: ["Subscriptions", "Credit Packs", "Add-ons"],
    sample_offer: {
      name: "20% off Pro Plan",
      description: "Take 20% off the first month of the Pro Plan for eligible Cyannect shoppers.",
      redemption_method: "AUTO_DISCOUNT",
      discount_type: "percent",
      discount_value: 20,
    },
  },
};

const STEPS = [
  { k: "identity",   label: "Business Identity",   icon: Store },
  { k: "destination",label: "Shopper Destination", icon: Globe },
  { k: "provinces",  label: "Province Coverage",   icon: MapPin },
  { k: "categories", label: "Categories",          icon: Layers },
  { k: "readiness",  label: "Integration",         icon: ShieldCheck },
  { k: "review",     label: "Review Storefront",   icon: Rocket },
];


function StatusIcon({ status }) {
  if (status === "complete" || status === "active" || status === "connected" || status === "VERIFIED") {
    return <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />;
  }
  if (status === "optional" || status === "not_configured") {
    return <Circle className="w-4 h-4 text-slate-300 shrink-0" />;
  }
  return <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />;
}


export default function StorefrontSetupGuide() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // MERCHANT-ONBOARDING-REVIEW-1 add-on · fast-track mode.
  // A merchant adding a subsequent storefront on Growth already knows
  // what a Storefront is — skip the intro copy and hide the first-time
  // education panels (rule §25 "shorter experience may be used for
  // subsequent Storefronts").
  const additional = searchParams.get("additional") === "1";
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null); // first sf if any
  const [readiness, setReadiness] = useState(null);
  const [verification, setVerification] = useState(null);
  const [categoriesInput, setCategoriesInput] = useState("");
  // Local form draft — used only to POST /api/v1/merchant/storefronts once.
  // Persistence source of truth = the created Storefront row (rule §22).
  const [form, setForm] = useState({
    name: "", primary_category: "retail", description: "", website_url: "",
    provinces: ["ON"], logo_url: "", banner_url: "",
  });

  // Rule §41 · reuse existing endpoints; rule §24 · don't force
  // existing merchants through the wizard. New: also hydrate a saved
  // wizard draft (per-user server persistence) so a fresh merchant can
  // close the tab and pick up exactly where they left off.
  useEffect(() => {
    (async () => {
      // 1) Prefer an existing Storefront (skips the wizard entirely) —
      // EXCEPT when the merchant is intentionally adding another one via
      // ?additional=1, in which case we let them enter Step 1 fresh.
      let hasStorefront = false;
      try {
        const r = await api.get("/v1/merchant/storefronts");
        const list = r.data?.storefronts || [];
        if (list.length && !additional) {
          hasStorefront = true;
          setExisting(list[0]);
          setForm((f) => ({
            ...f, name: list[0].name || "",
            primary_category: list[0].primary_category || "retail",
            description: list[0].description || "",
            website_url: list[0].website_url || "",
            provinces: list[0].provinces || ["ON"],
          }));
          setStep(5);
        }
      } catch { /* fall through */ }

      // 2) Hydrate a persisted wizard draft (only when there's no
      // storefront yet — otherwise the storefront row is source of truth).
      if (!hasStorefront) {
        try {
          const d = await api.get("/v1/merchant/storefront-setup/draft");
          const draft = d.data;
          if (draft && draft.user_id) {
            setForm((f) => ({
              ...f,
              name: draft.name || "",
              primary_category: draft.primary_category || "retail",
              description: draft.description || "",
              website_url: draft.website_url || "",
              provinces: draft.provinces && draft.provinces.length
                ? draft.provinces : ["ON"],
              logo_url: draft.logo_url || "",
              banner_url: draft.banner_url || "",
            }));
            if (draft.categories && draft.categories.length) {
              setCategoriesInput(draft.categories.join(", "));
            }
            if (typeof draft.step === "number") {
              setStep(Math.max(0, Math.min(4, draft.step)));
            }
            // Friendly recovery nudge — one toast, only when we actually
            // hydrated meaningful fields (a bare draft with just default
            // "ON" province + empty name isn't worth announcing).
            if ((draft.name || "").trim().length > 1
                || (draft.website_url || "").trim().length > 5
                || (draft.categories || []).length > 0) {
              toast.success(
                "Welcome back — resuming your Storefront draft from earlier",
                { id: "cy_setup_draft_resumed" },
              );
            }
          }
        } catch { /* no draft, no problem */ }
      }
    })();
  }, []);

  // Autosave the wizard draft whenever the merchant edits fields — only
  // BEFORE the storefront exists (steps 0-3). Debounced to avoid spamming
  // the API on every keystroke.
  useEffect(() => {
    if (existing) return;
    const t = setTimeout(() => {
      const cats = categoriesInput
        .split(/[,\n]/g).map((s) => s.trim()).filter(Boolean).slice(0, 20);
      api.put("/v1/merchant/storefront-setup/draft", {
        name: form.name || "",
        primary_category: form.primary_category || "retail",
        description: form.description || "",
        website_url: form.website_url || "",
        logo_url: form.logo_url || "",
        banner_url: form.banner_url || "",
        provinces: form.provinces || [],
        categories: cats,
        step,
      }).catch(() => {});
    }, 900);
    return () => clearTimeout(t);
  }, [form, categoriesInput, step, existing]);

  // Hydrate readiness + coupon verification whenever we have a storefront.
  useEffect(() => {
    if (!existing) return;
    (async () => {
      try {
        const r = await api.get(`/v1/merchant/storefront/readiness?sid=${existing.id}`);
        setReadiness(r.data);
      } catch { setReadiness(null); }
      try {
        const v = await api.get(`/v1/merchant/storefronts/${existing.id}/checkout-verification`);
        setVerification(v.data);
      } catch { setVerification({ status: "NOT_CHECKED" }); }
    })();
  }, [existing, step]);

  const canGoNext = useMemo(() => {
    if (step === 0) return form.name.trim().length >= 2 && form.primary_category;
    if (step === 1) return (form.website_url || "").trim().length > 5;
    if (step === 2) return (form.provinces || []).length > 0;
    return true;
  }, [step, form]);

  const createStorefront = async () => {
    setBusy(true);
    try {
      const cats = categoriesInput
        .split(/[,\n]/g).map((s) => s.trim()).filter(Boolean).slice(0, 20);
      const r = await api.post("/v1/merchant/storefronts", {
        name: form.name.trim(),
        primary_category: form.primary_category,
        description: form.description || "",
        website_url: form.website_url.trim(),
        provinces: form.provinces,
        logo_url: form.logo_url || "",
        banner_url: form.banner_url || "",
        categories: cats,
      });
      setExisting(r.data);
      toast.success("Storefront created — let's review readiness");
      // Wizard draft is no longer needed once the Storefront exists.
      api.delete("/v1/merchant/storefront-setup/draft").catch(() => {});
      setStep(4); // move to Integration readiness step
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Could not create storefront"));
    } finally { setBusy(false); }
  };

  // Apply a starter template (Retail / SaaS) to the wizard fields.
  // The sample-offer object is stashed under
  // localStorage["cy_sample_offer_prefill"] so the Manual Campaign creator
  // can offer to prefill it once the merchant clicks Create First Offer.
  const applyTemplate = (key) => {
    const t = TEMPLATES[key];
    if (!t) return;
    setForm((f) => ({
      ...f,
      name: t.identity.name,
      primary_category: t.identity.primary_category,
      description: t.identity.description,
    }));
    setCategoriesInput(t.categories.join(", "));
    try {
      localStorage.setItem("cy_sample_offer_prefill", JSON.stringify(t.sample_offer));
    } catch {}
    toast.success(`${t.label} loaded — tweak and continue.`);
  };

  const goNext = async () => {
    if (step === 3 && !existing) {
      // We collected Steps 1-4 client-side; now POST once to create.
      await createStorefront();
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const toggleProvince = (p) => {
    const cur = new Set(form.provinces || []);
    if (cur.has(p)) cur.delete(p); else cur.add(p);
    setForm({ ...form, provinces: Array.from(cur) });
  };

  const StepIcon = STEPS[step].icon;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6" data-testid="storefront-setup-guide">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-cyan-700">
            <Rocket className="w-4 h-4" /> Storefront Setup Guide
            {additional && !existing && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-cyan-100 text-cyan-800 text-[10px] tracking-widest"
                    data-testid="setup-fast-track-chip">
                Fast track
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl font-semibold text-slate-900 mt-1">
            {existing ? "Review your Storefront"
                     : additional ? "Add another Storefront"
                     : "Create your first Storefront"}
          </h1>
          {!additional && (
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              A Storefront is your shopper-facing business presence on Cyannect. It represents a brand, division, service line, or independently managed catalogue — <span className="font-medium">not simply a product category</span>.
            </p>
          )}
          {additional && !existing && (
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Skipping the basics — fill in the new Storefront's identity, destination, coverage and categories.
            </p>
          )}
        </div>
      </header>

      {/* Stepper */}
      <ol className="flex flex-wrap gap-1.5 md:gap-2" data-testid="setup-stepper">
        {STEPS.map((s, idx) => {
          const active = idx === step;
          const done = idx < step;
          return (
            <li key={s.k} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${active ? "pf-btn-primary" : done ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-slate-50 text-slate-500 ring-1 ring-slate-200"}`}
                data-testid={`setup-step-${s.k}`}>
              <span className="w-4 h-4 rounded-full bg-white/60 grid place-items-center text-[10px]">{idx + 1}</span>
              {s.label}
            </li>
          );
        })}
      </ol>

      <div className="text-xs text-slate-500" data-testid="setup-progress">Step {step + 1} of {STEPS.length}</div>

      {/* Step body */}
      <div className="pf-card p-5 md:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <StepIcon className="w-5 h-5 text-slate-700" />
          <h2 className="font-display text-xl font-semibold text-slate-900">{STEPS[step].label}</h2>
        </div>

        {step === 0 && (
          <div className="space-y-3" data-testid="setup-body-identity">
            {!existing && !additional && (
              <div className="pf-card p-3 mb-2 border-cyan-200/60 bg-cyan-50/40"
                   data-testid="setup-templates">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cyan-800 font-semibold">
                  <Sparkles className="w-3.5 h-3.5" /> Start from an example
                </div>
                <p className="text-[11px] text-slate-600 mt-1">
                  Load a Retail or SaaS example so you can tweak instead of start from scratch. You'll still review and confirm every step — nothing is auto-published.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(TEMPLATES).map(([k, t]) => {
                    const Icon = t.icon;
                    return (
                      <button key={k} type="button"
                              onClick={() => applyTemplate(k)}
                              className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                              data-testid={`setup-template-${k}`}>
                        <Icon className="w-3.5 h-3.5" /> {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Storefront name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="pf-input w-full px-3 py-2 mt-1 text-sm"
                     placeholder="e.g. ABC Fashion"
                     data-testid="setup-name" />
              <p className="text-[11px] text-slate-500 mt-1">Use the brand or business name shoppers will recognize.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Primary category *</label>
              <select value={form.primary_category}
                      onChange={(e) => setForm({ ...form, primary_category: e.target.value })}
                      className="pf-input w-full px-3 py-2 mt-1 text-sm"
                      data-testid="setup-primary-category">
                {PRIMARY_CATEGORIES.map((c) => (
                  <option key={c.v} value={c.v}>{c.l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Short description</label>
              <textarea value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        rows={3}
                        className="pf-input w-full px-3 py-2 mt-1 text-sm"
                        placeholder="What does your business sell or provide?"
                        data-testid="setup-description" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Logo URL</label>
                <input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                       className="pf-input w-full px-3 py-2 mt-1 text-sm"
                       placeholder="https://…"
                       data-testid="setup-logo-url" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Banner URL</label>
                <input value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })}
                       className="pf-input w-full px-3 py-2 mt-1 text-sm"
                       placeholder="https://…"
                       data-testid="setup-banner-url" />
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3" data-testid="setup-body-destination">
            <p className="text-sm text-slate-600">Shoppers discover your Offers in Cyannect and complete their purchase on your own website or checkout.</p>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Website URL *</label>
              <input value={form.website_url}
                     onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                     className="pf-input w-full px-3 py-2 mt-1 text-sm"
                     placeholder="https://yourshop.com"
                     data-testid="setup-website" />
              <p className="text-[11px] text-slate-500 mt-1">A functioning website checkout is enough — you do not need your own mobile app.</p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="setup-body-provinces">
            <p className="text-sm text-slate-600">Province Coverage determines where Cyannect focuses your campaign visibility and customer-acquisition activity. Start focused, measure performance, and expand region by region.</p>
            <div className="flex flex-wrap gap-1.5" data-testid="setup-provinces">
              {ALL_PROV.map((p) => (
                <button key={p} type="button"
                        onClick={() => toggleProvince(p)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${form.provinces.includes(p) ? "pf-btn-primary" : "pf-btn-glass"}`}
                        data-testid={`setup-province-${p}`}>
                  {p}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500">Add-on Province pricing (if any) follows your existing plan entitlement — not modified by this wizard.</p>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="setup-body-categories">
            <p className="text-sm text-slate-600">Categories organize products and Offers inside your Storefront. You usually do not need a separate Storefront for each category.</p>
            {!additional && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div className="pf-card p-3">
                  <div className="font-semibold text-slate-900">Retail example</div>
                  <div className="text-slate-600 mt-1">Storefront: <span className="font-medium">ABC Fashion</span></div>
                  <div className="text-slate-600">Categories: Men · Women · Shoes</div>
                </div>
                <div className="pf-card p-3">
                  <div className="font-semibold text-slate-900">SaaS example</div>
                  <div className="text-slate-600 mt-1">Storefront: <span className="font-medium">Example AI</span></div>
                  <div className="text-slate-600">Categories: Subscriptions · Credit Packs · Add-ons</div>
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Categories (optional, comma-separated)</label>
              <textarea value={categoriesInput}
                        onChange={(e) => setCategoriesInput(e.target.value)}
                        rows={2}
                        className="pf-input w-full px-3 py-2 mt-1 text-sm"
                        placeholder="e.g. Men, Women, Shoes"
                        data-testid="setup-categories" />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3" data-testid="setup-body-readiness">
            {!readiness && (
              <p className="text-sm text-slate-600">Loading readiness…</p>
            )}
            {readiness && (
              <>
                <p className="text-sm text-slate-600">
                  Reusing Cyannect's existing readiness engine. Only genuinely required items block your Storefront from being ready.
                </p>
                <ul className="space-y-1.5">
                  {readiness.checklist.map((it) => (
                    <li key={it.label}
                        className="flex items-start gap-2 text-sm"
                        data-testid={`setup-readiness-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <StatusIcon status={it.status} />
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">
                          {it.label}
                          <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{it.status.replace(/_/g, " ")}</span>
                        </div>
                        {it.hint && <div className="text-xs text-slate-600 mt-0.5">{it.hint}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
                {/* Coupon Code readiness — linked to existing checkout-verification flow. */}
                <div className="pf-card p-3 mt-3" data-testid="setup-coupon-readiness">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                      <StatusIcon status={verification?.status === "VERIFIED" ? "VERIFIED" : "incomplete"} />
                      Coupon Code checkout
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold ml-1">
                        {(verification?.status || "NOT_CHECKED").replace(/_/g, " ").toLowerCase()}
                      </span>
                    </div>
                    <Link to="/merchant/checkout-verification"
                          className="text-xs font-semibold text-cyan-700 hover:text-cyan-800 inline-flex items-center gap-1"
                          data-testid="setup-coupon-verify-link">
                      Manage <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Automatic Discount offers work without checkout verification. Coupon Code offers need a Verified checkout before publish.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4" data-testid="setup-body-review">
            {!existing && (
              <p className="text-sm text-slate-600">Save your storefront to review readiness.</p>
            )}
            {existing && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="pf-card p-3" data-testid="review-identity">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Identity</div>
                    <div className="font-medium text-slate-900 mt-1">{existing.name}</div>
                    <div className="text-xs text-slate-600">{existing.primary_category}</div>
                  </div>
                  <div className="pf-card p-3" data-testid="review-destination">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Destination</div>
                    <div className="font-medium text-slate-900 mt-1 break-all">{existing.website_url || "—"}</div>
                  </div>
                  <div className="pf-card p-3" data-testid="review-provinces">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Province Coverage</div>
                    <div className="text-slate-900 mt-1">{(existing.provinces || []).join(" · ") || "—"}</div>
                  </div>
                  <div className="pf-card p-3" data-testid="review-categories">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Categories</div>
                    <div className="text-slate-900 mt-1">{(existing.categories || []).join(" · ") || "—"}</div>
                  </div>
                </div>

                {readiness && (
                  <div className={`pf-card p-4 ${readiness.ready ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60"}`}
                       data-testid={readiness.ready ? "setup-ready-card" : "setup-not-ready-card"}>
                    <div className="flex items-center gap-2">
                      {readiness.ready
                        ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        : <AlertCircle className="w-5 h-5 text-amber-600" />}
                      <div className="font-semibold text-slate-900">
                        {readiness.ready ? "Your Storefront is ready" : "Complete these items before creating your first live Offer"}
                      </div>
                    </div>
                    {readiness.ready && (
                      <p className="text-sm text-slate-700 mt-2">You can now create your first Offer.</p>
                    )}
                    {!readiness.ready && (
                      <ul className="mt-2 space-y-1 text-sm">
                        {readiness.checklist.filter((c) => c.blocking && c.status !== "complete").map((c) => (
                          <li key={c.label} className="flex items-start gap-2"
                              data-testid={`setup-blocking-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                            <div>
                              <div className="font-medium">{c.label}</div>
                              <div className="text-xs text-slate-600">{c.hint || "Needs attention."}</div>
                            </div>
                            <Link to="/merchant/storefronts"
                                  className="text-xs font-semibold text-cyan-700 hover:text-cyan-800 ml-auto">
                              Fix →
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  <Link to="/merchant/storefronts"
                        className="pf-btn-glass px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5"
                        data-testid="setup-manage-storefront">
                    <Info className="w-3.5 h-3.5" /> Manage Storefront
                  </Link>
                  <button type="button"
                          onClick={() => navigate("/merchant/campaigns")}
                          className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5"
                          disabled={!readiness?.ready}
                          data-testid="setup-create-first-offer">
                    <Rocket className="w-3.5 h-3.5" /> Create First Offer
                  </button>
                </div>
                <p className="text-[11px] text-slate-500">
                  This will take you to the Manual Campaign creator. Your new Offer starts as a Draft, then goes through Review & Publish before it's live.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="flex justify-between gap-2">
        <button type="button"
                onClick={goBack}
                disabled={step === 0 || busy}
                className="pf-btn-glass px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
                data-testid="setup-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        {step < STEPS.length - 1 && (
          <button type="button"
                  onClick={goNext}
                  disabled={!canGoNext || busy}
                  className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
                  data-testid="setup-next">
            {step === 3 && !existing ? (busy ? "Saving…" : "Save Storefront →") : "Next"}
            {!(step === 3 && !existing) && <ArrowRight className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}
